// (c) 2026 Owl Consultations. All rights reserved.
// D-CRX-42 WS15: Pharmacy Settings → Site of Care. Two halves of one concept:
// the TYPES (which options enrollment offers; requires_facility is DATA, SOC-2)
// and the FACILITY DIRECTORY, one list per type (FD-13/14: sub-nav generated
// from the type registry, never hard-coded). Delete is soft, always.
// Styling: app inline-style idiom (CSS vars) — this project has no Tailwind.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Pencil, Building2, Upload } from 'lucide-react'
import {
  listSiteOfCareTypes, updateSiteOfCareType, addSiteOfCareType,
  listFacilities, upsertFacility, setFacilityActive, type FacilityUpsertInput,
  type SiteOfCareType, type FacilityRow,
} from '../../api/client'
import SearchableSelect from '../../components/common/SearchableSelect'
import { useResolveAllSiteOfCare, useInvalidateSiteOfCare, useSiteOfCareSummary } from '../../hooks/queries/siteOfCare'
import { friendlyError } from '../../api/errors'
import { useServerFunnels, type ServerFunnelColumn } from '../../components/common/TableFunnels'

const RESIDENTIAL = ['snf', 'board_care']

const T = {
  text: 'var(--color-text, #1a1a1a)', soft: 'var(--color-text-soft, #6b7280)',
  mute: 'var(--color-text-mute, #9ca3af)', border: 'var(--color-border, #e5e7eb)',
  surface: 'var(--color-surface, #fff)', primary: 'var(--color-primary, #185FA5)',
  okBg: '#E1F5EE', okFg: '#085041', warnBg: '#fef3c7', warnFg: '#92400e',
}
const card: React.CSSProperties = { background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 12, padding: '14px 16px', marginBottom: 12 }
// Sticky: with 54 facilities imported you lose the header within one screen of scrolling, and a
// 14-column table whose headings have scrolled away is unreadable.
const th: React.CSSProperties = { textAlign: 'left', padding: '9px 12px', fontWeight: 500, fontSize: 12.5, color: T.soft, borderBottom: `0.5px solid ${T.border}`, whiteSpace: 'nowrap', position: 'sticky', top: 0, background: T.surface, zIndex: 1 }
const td: React.CSSProperties = { padding: '9px 12px', borderBottom: `0.5px solid ${T.border}`, fontSize: 13, verticalAlign: 'middle' }
const btn: React.CSSProperties = { fontSize: 12.5, padding: '6px 11px', border: `0.5px solid ${T.border}`, borderRadius: 8, background: 'transparent', color: T.text, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }
const btnPrimary: React.CSSProperties = { ...btn, background: T.primary, color: '#fff', border: 'none', fontWeight: 600 }
const btnSm: React.CSSProperties = { ...btn, fontSize: 11.5, padding: '3px 9px' }
const pill = (bg: string, fg: string): React.CSSProperties => ({ fontSize: 11, padding: '2px 10px', borderRadius: 20, background: bg, color: fg, whiteSpace: 'nowrap' })
const fieldLabel: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12.5, color: T.soft, marginBottom: 8 }
const input: React.CSSProperties = { height: 34, padding: '0 10px', border: `0.5px solid ${T.border}`, borderRadius: 8, background: T.surface, color: T.text, fontSize: 13 }

export default function SiteOfCareTab() {
  const [types, setTypes] = useState<SiteOfCareType[]>([])
  const [facs, setFacs] = useState<FacilityRow[]>([])
  const [tab, setTab] = useState<string>('types')
  const [showInactive, setShowInactive] = useState(false)
  const [editFac, setEditFac] = useState<Partial<FacilityRow> | null>(null)
  const [editType, setEditType] = useState<Partial<SiteOfCareType> & { isNew?: boolean } | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // D-SOC-1: how many Rx currently map to each facility, and the re-run control.
  const summaryQ = useSiteOfCareSummary()
  const resolveAll = useResolveAllSiteOfCare()
  const invalidateSoc = useInvalidateSiteOfCare()
  const [resolveNote, setResolveNote] = useState<string | null>(null)
  const rxByFacility = useMemo(() => Object.fromEntries((summaryQ.data?.items ?? []).map(i => [i.facility_id, i])), [summaryQ.data])

  // ── The sweep ────────────────────────────────────────────────────────────────────────────
  // /site-of-care/resolve is BOUNDED by design: one batch per request, because an unbounded
  // pass over a 54k-Rx tenant ran ~451s against a 600s function timeout on a 0.5-1 vCore
  // serverless DB. The button used to fire exactly ONE batch and stop, which on ASP meant
  // 2,000 of 54,459 Rx and no indication that anything remained — Mario, 2026-09-23:
  // "i dont have to click 2000 times". The loop belongs here, not in the operator's hands.
  //
  // `running` drives the loop; `pausedRef` is read INSIDE it, so Pause takes effect at the
  // next batch boundary rather than abandoning an in-flight request (a batch that is already
  // stamping rows should be allowed to finish and report).
  const [sweep, setSweep] = useState<{ done: number; remaining: number | null; byType: Record<string, number> } | null>(null)
  const [running, setRunning] = useState(false)
  const [paused, setPaused] = useState(false)
  const pausedRef = useRef(false)
  const cancelRef = useRef(false)
  // Whether the sweep in progress is a FORCED one. Resume must carry this forward: resuming a
  // forced sweep with the default predicate would select nothing and report success.
  const forcedRef = useRef(false)
  // The forced sweep's cursor, and it MUST outlive a single runSweep call. It was a local
  // `let cursor` inside runSweep, so Resume re-entered with undefined, the server fell back to
  // `DateTimeOffset.UtcNow`, and every row stamped before the pause satisfied
  // `resolved_at < @since` again. Since the batch query orders by the STALEST first, the resumed
  // run re-selected exactly the rows it had just finished and never reached the unresolved ones:
  // `remaining` never fell to 0 and the loop ran forever against a 0.5-1 vCore DB.
  const cursorRef = useRef<string | undefined>(undefined)
  const setPausedBoth = (v: boolean) => { pausedRef.current = v; setPaused(v) }

  const runSweep = useCallback(async (resume: boolean, force = false) => {
    // On resume, keep whatever the interrupted run was doing.
    if (!resume) forcedRef.current = force
    force = forcedRef.current
    cancelRef.current = false
    setPausedBoth(false)
    setRunning(true)
    setErr(null)
    if (!resume) {
      setSweep({ done: 0, remaining: null, byType: {} })
      setResolveNote(null)
      cursorRef.current = undefined      // a NEW run starts a new cursor; a resume keeps the old one
    }
    let pausedOut = false
    try {
      for (;;) {
        if (cancelRef.current) break
        // Pause leaves the loop WITHOUT finishing the sweep: the tally stays on screen and
        // Resume continues under the SAME cursor (cursorRef), so no work is repeated or lost.
        if (pausedRef.current) { pausedOut = true; break }
        const r = await resolveAll.mutateAsync({ batch: 2000, force, since: cursorRef.current })
        // Pin the cursor on the FIRST batch and keep it for the whole run, across pause/resume.
        if (force && r.since && !cursorRef.current) cursorRef.current = r.since
        setSweep(prev => {
          const byType = { ...(prev?.byType ?? {}) }
          for (const [k, v] of Object.entries(r.by_type)) byType[k] = (byType[k] ?? 0) + v
          return { done: (prev?.done ?? 0) + r.rx_resolved, remaining: r.remaining, byType }
        })
        if (r.done || r.rx_resolved === 0) {
          // rx_resolved === 0 guards against a non-advancing server: without it a batch that
          // stamps nothing but still reports work remaining would spin this loop forever.
          setResolveNote(r.done ? 'Address matching complete.' : 'Stopped: the last batch resolved nothing.')
          break
        }
      }
    } catch (e) {
      setErr(friendlyError(e))
    } finally {
      setRunning(false)
      // Refresh the summary ONCE, when the sweep actually ends — not after every batch (which
      // would refetch ~27 times on a 54k tenant) and not on a pause, where the numbers are
      // deliberately mid-flight and about to change again on Resume.
      if (!pausedOut) invalidateSoc()
    }
  }, [resolveAll, invalidateSoc])

  const pauseSweep = () => setPausedBoth(true)
  const stopSweep = () => { cancelRef.current = true; setPausedBoth(false); cursorRef.current = undefined }

  // ── CSV import ───────────────────────────────────────────────────────────────────────────
  // Mario, 2026-09-23: "the file should upload on the active tab i am at like SNF tab is active
  // then the file loads in them" and "import verbatim let me edit later".
  //
  // So: the facility TYPE is never read from the file — it is the tab you are standing on. And
  // values go in EXACTLY as written. No trimming of "Formerly: …" annotations, no de-duplicating
  // near-identical names, no address repair. The CDI list has all three problems; guessing which
  // ones to fix silently is how a directory ends up wrong in a way nobody can see. Import it as
  // written, then edit it in the table.
  const [importRows, setImportRows] = useState<FacilityUpsertInput[] | null>(null)
  // Which previewed rows will actually be written. A row with NO address is almost always a
  // section header in a hand-kept list ("Los Angeles County", "Antelope Valley") rather than a
  // facility — the CDI file has several. They are shown, flagged and UNTICKED by default, never
  // silently dropped: this is an import you can see, and the call is yours.
  const [importSkip, setImportSkip] = useState<Set<number>>(new Set())
  const [importName, setImportName] = useState('')
  const [importing, setImporting] = useState(false)
  const [importDone, setImportDone] = useState<{ added: number; failed: { name: string; why: string }[] } | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)

  /** RFC4180-ish split of ONE line: honours quoted fields and "" escapes. */
  const splitCsvLine = (line: string): string[] => {
    const out: string[] = []; let cur = ''; let q = false
    for (let i = 0; i < line.length; i++) {
      const c = line[i]
      if (q) {
        if (c === '"') { if (line[i + 1] === '"') { cur += '"'; i++ } else q = false }
        else cur += c
      } else if (c === '"') q = true
      else if (c === ',') { out.push(cur); cur = '' }
      else cur += c
    }
    out.push(cur)
    return out
  }

  /**
   * Header-driven so column ORDER does not matter and a file with extra columns still works.
   * Recognised headers (case/space-insensitive): name, address/address line, city, state,
   * zip/postal code, phone, contact name/phone/email, hours, cutoff, wing, room, bed, notes.
   * A row with no name is skipped — that is what the CDI file's region separators and address
   * continuation lines look like.
   */
  const parseCsv = (text: string): FacilityUpsertInput[] => {
    const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter(l => l.trim().length > 0)
    if (lines.length < 2) return []
    const key = (h: string) => h.toLowerCase().replace(/[^a-z]/g, '')
    const head = splitCsvLine(lines[0]).map(key)
    const at = (names: string[]) => { for (const n of names) { const i = head.indexOf(n); if (i >= 0) return i } return -1 }
    const ix = {
      name:    at(['facilityname', 'facilitynames', 'name']),
      address: at(['address', 'addressline', 'street', 'addressline1']),
      city:    at(['city']),
      state:   at(['state', 'statecode']),
      zip:     at(['zip', 'zipcode', 'postalcode']),
      phone:   at(['phone', 'facilityphone', 'primaryphone', 'telephone']),
      cname:   at(['contactname', 'contact']),
      cphone:  at(['contactphone']),
      cemail:  at(['contactemail', 'email']),
      hours:   at(['operatinghours', 'hours']),
      cutoff:  at(['deliverycutofftime', 'deliverycutoff', 'cutoff']),
      wing:    at(['wing']), room: at(['room']), bed: at(['bed']), notes: at(['notes']),
    }
    if (ix.name < 0) return []
    const val = (cells: string[], i: number) => (i >= 0 && i < cells.length ? cells[i].trim() : '')
    const rows: FacilityUpsertInput[] = []
    for (let n = 1; n < lines.length; n++) {
      const c = splitCsvLine(lines[n])
      const name = val(c, ix.name)
      if (!name) continue                       // region header / address continuation row
      rows.push({
        name,
        facility_type: activeType?.code ?? facTypes[0]?.code ?? null,
        address_line: val(c, ix.address) || null,
        city: val(c, ix.city) || null,
        state_code: val(c, ix.state) || null,
        zip_code: val(c, ix.zip) || null,
        phone: val(c, ix.phone) || null,
        contact_name: val(c, ix.cname) || null,
        contact_phone: val(c, ix.cphone) || null,
        contact_email: val(c, ix.cemail) || null,
        operating_hours: val(c, ix.hours) || null,
        delivery_cutoff_time: val(c, ix.cutoff) || null,
        wing: val(c, ix.wing) || null,
        room: val(c, ix.room) || null,
        bed: val(c, ix.bed) || null,
        notes: val(c, ix.notes) || null,
      })
    }
    return rows
  }

  const onPickFile = async (file: File) => {
    setImportDone(null)
    const text = await file.text()
    const rows = parseCsv(text)
    setImportName(file.name)
    if (rows.length === 0) {
      setErr('No facilities found in that file. The first row must be a header containing a name column.')
      setImportRows(null)
    } else {
      setImportRows(rows)
      setImportSkip(new Set(rows.map((r, i) => (r.address_line ? -1 : i)).filter(i => i >= 0)))
    }
  }

  // Sequential, not Promise.all: ASP's tenant DB is 0.5-1 vCore serverless and a parallel burst
  // is what took it down on 2026-08-06. A hundred facilities at ~50ms is a few seconds.
  const runImport = async () => {
    if (!importRows) return
    setImporting(true)
    const failed: { name: string; why: string }[] = []
    let added = 0
    for (let i = 0; i < importRows.length; i++) {
      if (importSkip.has(i)) continue
      const row = importRows[i]
      try { await upsertFacility(row); added++ }
      catch (e) {
        const body = (e as { body?: { error?: string; message?: string } })?.body
        failed.push({ name: row.name, why: body?.message || body?.error || friendlyError(e) })
      }
    }
    setImporting(false)
    setImportRows(null)
    setImportDone({ added, failed })
    await reload()
    // The DIRECTORY just changed, so every Rx may now resolve differently — including rows this
    // resolver version already stamped. A normal sweep would select none of them and report
    // success having done nothing (exactly what Mario saw after importing 54 facilities), so an
    // import runs a FORCED sweep.
    if (added > 0) await runSweep(false, true)
  }

  // ── Server-side paging + search ──────────────────────────────────────────────────────────
  // Mario, 2026-09-23: "there is no pagination or anything in the loading for the faiclities"
  // and "it must be server and client side also the search must be added correctly to the same
  // api and the ui search box".
  //
  // The list used to fetch EVERY facility and filter the array in the browser — the exact
  // fetch-all-then-filter pattern ENGINEERING_STANDARDS prohibits, and unusable at 54 rows with
  // no ceiling above that. Page, search, sort and the total now all come from the server, on the
  // SAME endpoint. `total` is counted under the same predicate as the page, so the pager cannot
  // claim a number the rows disagree with.
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')      // what is typed
  const [q, setQ] = useState('')                // what has been SENT (debounced)
  const [sort, setSort] = useState<'name' | 'city' | 'zip' | 'type' | 'used_by'>('name')
  const [dir, setDir] = useState<'asc' | 'desc'>('asc')
  const [loadingList, setLoadingList] = useState(false)

  // Debounce: a request per keystroke would hammer a 0.5-1 vCore tenant DB.
  useEffect(() => {
    const t = setTimeout(() => { setQ(search.trim()); setPage(1) }, 300)
    return () => clearTimeout(t)
  }, [search])

  const facTypes = useMemo(() => types.filter(t => t.requires_facility), [types])

  // D-UI-2: header funnels. The directory is SERVER-paged, so a funnel only collects what was
  // asked; GET /site-of-care/facilities applies it to its page query and its COUNT. The column
  // set follows the headers the tab renders (Type only on "All facilities", Wing/Room/Bed only on
  // the residential types), so a filter on a column that is no longer shown stops applying.
  // "Rx mapped" is shown from /site-of-care/summary; its filter is that same count, read per
  // facility by the list query.
  const funnelCols = useMemo<ServerFunnelColumn[]>(() => {
    const oneType = tab !== 'types' && tab !== 'all' ? types.find(t => t.code === tab) ?? null : null
    const out: ServerFunnelColumn[] = [{ key: 'name', label: 'Name', kind: 'text' }]
    // The registry IS the vocabulary; the cell shows the short label, the server matches the code.
    if (!oneType) out.push({ key: 'type', label: 'Type', kind: 'list',
      options: types.map(t => ({ value: t.code, label: t.label.split(' — ')[0] })) })
    out.push(
      { key: 'address', label: 'Address', kind: 'text' },
      { key: 'city_state_zip', label: 'City / State / Zip', kind: 'text' },
      { key: 'phone', label: 'Phone', kind: 'text' })
    if (oneType && RESIDENTIAL.includes(oneType.code)) out.push({ key: 'wing_room_bed', label: 'Wing / Room / Bed', kind: 'text' })
    out.push(
      { key: 'contact', label: 'Contact', kind: 'text' },
      { key: 'hours', label: 'Hours', kind: 'text' },
      { key: 'delivery_cutoff', label: 'Delivery cutoff', kind: 'text' },
      { key: 'source', label: 'Source', kind: 'list', options: [
        { value: 'pioneer', label: 'PioneerRx' }, { value: 'local', label: 'Pharmacy-managed' }] },
      { key: 'rx_mapped', label: 'Rx mapped', kind: 'number' },
      { key: 'used_by', label: 'Used by', kind: 'number' },
      { key: 'status', label: 'Status', kind: 'list', options: [
        { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }] })
    return out
  }, [tab, types])
  const fx = useServerFunnels(funnelCols)
  // The request reads the filter through a ref and refetches on `fx.sig`: the array is rebuilt
  // whenever the type registry reloads, and an identical filter must not refetch the page.
  const filterRef = useRef(fx.filter)
  filterRef.current = fx.filter

  const reload = useCallback(async () => {
    try {
      const t = await listSiteOfCareTypes()
      setTypes(t.items); setErr(null)
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed to load') }
  }, [])
  useEffect(() => { void reload() }, [reload])

  // The facility PAGE reloads whenever anything the server needs changes.
  const loadFacilities = useCallback(async () => {
    if (tab === 'types') return                 // the types tab does not render the directory
    setLoadingList(true)
    try {
      const f = await listFacilities(
        tab === 'all' ? undefined : tab,
        !showInactive,                          // "show inactive" off => ask the server for active only
        { page, pageSize, q, sort, dir, filter: filterRef.current })
      setFacs(f.items)
      setTotal(f.total ?? f.items.length)
      setErr(null)
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed to load facilities') }
    finally { setLoadingList(false) }
  }, [tab, showInactive, page, pageSize, q, sort, dir, fx.sig])
  useEffect(() => { void loadFacilities() }, [loadFacilities])

  // Changing tab or the inactive toggle resets to page 1 — page 4 of SNF is meaningless on AIC.
  // So does a funnel: page 4 of the unfiltered list is not page 4 of the filtered one.
  useEffect(() => { setPage(1) }, [tab, showInactive, fx.sig])

  // The server already applied type, active and search, so this is the page as returned.
  const listFor = (_code: string | null) => facs

  /** A sortable column heading. Sorting is SERVER-side (whitelisted there), so this only sets
   *  state; the effect refetches. Clicking the active column flips direction.
   *
   *  D-UI-2: CALLED as a function in the header row, not mounted as an element. It is declared
   *  inside this component, so as an element type it is a new component on every render and React
   *  remounts the cell, which would close the funnel it now holds on each keystroke in Min / Max.
   *  The funnel stops its own click from reaching this cell, so opening it does not sort. */
  const SortTh = ({ col, funnel, children }: { col: 'name' | 'city' | 'zip' | 'type' | 'used_by'; funnel: string; children: React.ReactNode }) => (
    <th key={col} style={{ ...th, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => { if (sort === col) setDir(d => (d === 'asc' ? 'desc' : 'asc')); else { setSort(col); setDir('asc') } setPage(1) }}
        title="Sort by this column"
        aria-sort={sort === col ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      {children}{sort === col && <span style={{ marginLeft: 4, opacity: .7 }}>{dir === 'asc' ? '▲' : '▼'}</span>}
      {fx.funnel(funnel)}
    </th>
  )
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try { await fn(); await reload(); return true }
    catch (e) { setErr(e instanceof Error ? e.message : 'Save failed'); return false }
    finally { setBusy(false) }
  }
  const saveTypeModal = async () => {
    if (!editType) return
    const ok = editType.isNew
      ? !!editType.label?.trim() && await run(() => addSiteOfCareType({
          code: (editType.code || editType.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 32),
          label: editType.label!.trim(), requires_facility: editType.requires_facility ?? false }))
      : await run(() => updateSiteOfCareType(editType.code!, {
          label: editType.label?.trim() || undefined, notes: editType.notes ?? undefined,
          requires_facility: editType.is_protected ? undefined : editType.requires_facility }))
    if (ok) setEditType(null)
  }
  const toggleFac = (f: FacilityRow) => {
    if (f.is_active && f.used_by > 0 &&
      !window.confirm(`${f.name} is referenced by ${f.used_by} enrollment(s).\n\nDeactivating hides it from new enrollments; historical records keep resolving it. Continue?`)) return
    void run(() => setFacilityActive(f.id, !f.is_active))
  }
  const saveFacModal = async () => {
    if (!editFac?.name?.trim()) { setErr('Facility name is required'); return }
    if (!editFac.facility_type) { setErr('Pick the facility type'); return }
    // No `as` cast. The cast that stood here defeated the type it was casting to: it let the
    // modal send fields FacilityUpsertInput did not declare, and hid the fact that five edited
    // fields were being dropped on the way out (and nulled on the way in).
    if (await run(() => upsertFacility(editFac as FacilityUpsertInput))) setEditFac(null)
  }

  const activeType = tab !== 'types' && tab !== 'all' ? types.find(t => t.code === tab) ?? null : null
  const showResidential = activeType != null && RESIDENTIAL.includes(activeType.code)
  const short = (l: string) => l.split(' — ')[0]

  const subTabs = [
    { code: 'types', label: 'Types & settings', n: null as number | null },
    { code: 'all', label: 'All facilities', n: facs.filter(f => f.is_active).length },
    ...facTypes.map(t => ({ code: t.code, label: short(t.label), n: t.facility_count })),
  ]

  return (
    // maxWidth was 1200. That is a reasonable cap for a settings FORM and a bad one for this
    // page: the facility table carries 14 columns, so on a wide monitor the content was squeezed
    // into 1200px and then scrolled horizontally INSIDE that — with 54 facilities imported it
    // became unusable. The page now uses the window it is given; the table's own container keeps
    // the horizontal scroll for genuinely narrow screens.
    <div style={{ padding: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Building2 size={18} /> Site of Care</h2>
        <span style={{ fontSize: 12.5, color: T.soft }}>the places care is delivered, and the rules each carries</span>
      </div>
      <p style={{ margin: '0 0 14px', fontSize: 12.5, color: T.mute }}>
        Types control the enrollment dropdown; each facility-taking type manages its own list. Changes apply to new enrollments immediately.
      </p>
      {err && <div style={{ ...card, background: '#fef2f2', border: '0.5px solid #fca5a5', color: '#b91c1c', fontSize: 13 }}>{err}</div>}

      {/* D-SOC-1: address matching status + re-run */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600 }}>Rx address matching</div>
          <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>
            Every prescription is checked against this directory as it arrives. A match stamps the Rx with the facility type, name and phone;
            no match means residential. Re-run after editing addresses or importing a list.
            {summaryQ.data?.available && (
              <> Currently: <b>{(summaryQ.data.total_rx - summaryQ.data.residential_rx).toLocaleString()}</b> Rx at facilities,{' '}
              <b>{summaryQ.data.residential_rx.toLocaleString()}</b> residential.</>
            )}
          </div>
          {/* Live progress. `remaining` is the server's own count, so the total is derived rather
              than assumed — a sweep that finds more work than expected still reads correctly. */}
          {sweep && (
            <div style={{ fontSize: 12.5, color: T.soft, marginTop: 6 }}>
              {(() => {
                const total = sweep.remaining == null ? null : sweep.done + sweep.remaining
                const pct = total && total > 0 ? Math.round((sweep.done / total) * 100) : null
                const parts = Object.entries(sweep.byType).sort((a, b) => b[1] - a[1])
                  .map(([t, n]) => `${n.toLocaleString()} ${t}`).join(', ')
                return (<>
                  <div style={{ height: 6, borderRadius: 3, background: T.border, overflow: 'hidden', margin: '4px 0 6px', maxWidth: 420 }}>
                    <div style={{ height: '100%', width: `${pct ?? 0}%`, background: T.okFg, transition: 'width .3s' }} />
                  </div>
                  <b>{sweep.done.toLocaleString()}</b> re-checked
                  {total != null && <> of <b>{total.toLocaleString()}</b>{pct != null && <> ({pct}%)</>}</>}
                  {sweep.remaining != null && sweep.remaining > 0 && <> — {sweep.remaining.toLocaleString()} to go</>}
                  {parts && <><br />{parts}</>}
                  {paused && <> <b style={{ color: T.okFg }}>· paused</b></>}
                </>)
              })()}
            </div>
          )}
          {resolveNote && <div style={{ fontSize: 12.5, color: T.okFg, marginTop: 6 }}>{resolveNote}</div>}
        </div>
        {/* Run / Pause / Resume / Stop. The sweep drives itself to completion; pause takes effect
            at the next batch boundary so an in-flight batch is never abandoned mid-stamp. */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          {running ? (<>
            <button style={btn} onClick={pauseSweep}>Pause</button>
            <button style={btn} onClick={stopSweep}>Stop</button>
          </>) : paused ? (<>
            <button style={btnPrimary} onClick={() => void runSweep(true)}>Resume</button>
            <button style={btn} onClick={stopSweep}>Stop</button>
          </>) : (
            // FORCED (runSweep's second argument). The default predicate only re-resolves rows
            // whose resolver_version differs from the current one — it assumes the ALGORITHM is
            // the only input that can change an answer. The DIRECTORY is the other one, and it is
            // what changes when a pharmacy adds facilities. Pressing this button means "check
            // every prescription against the directory as it stands now", so it always forces:
            // the version-gated path would look at 54k already-stamped rows and report success
            // having selected none of them. Manual pins are still never overwritten.
            <button style={btnPrimary} onClick={() => void runSweep(false, true)}
                    title="Re-checks every prescription against the current facility directory">
              Re-run address matching
            </button>
          )}
        </div>
      </div>

      {/* sub-nav — generated from the registry (FD-14) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14 }}>
        {subTabs.map(b => (
          <button key={b.code} onClick={() => setTab(b.code)}
            style={tab === b.code ? { ...btnPrimary } : { ...btn }}>
            {b.label}{b.n != null && <span style={{ opacity: .75 }}>({b.n})</span>}
          </button>))}
      </div>

      {tab === 'types' ? (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 12 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 600 }}>Site-of-care types</h3>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: T.soft }}>
                “Requires facility” decides whether enrollment demands a pick from that type's directory. Home is protected — it always uses the patient's own address.</p>
            </div>
            <button style={btnPrimary} disabled={busy} onClick={() => setEditType({ isNew: true, requires_facility: true })}>
              <Plus size={13} /> Add type</button>
          </div>
          <div style={{ overflowX: 'auto', border: `0.5px solid ${T.border}`, borderRadius: 10 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr>
                <th style={th}>Label</th><th style={th}>Requires facility</th>
                <th style={{ ...th, textAlign: 'center' }}>Facilities</th><th style={th}>Status</th>
                <th style={th}>Notes</th><th data-no-funnel="row actions" style={{ ...th, textAlign: 'right' }}>Actions</th></tr></thead>
              <tbody>
                {types.map(t => (
                  <tr key={t.code} style={{ opacity: t.is_active ? 1 : .5 }}>
                    <td style={td}><b>{short(t.label)}</b>
                      {t.label.includes(' — ') && <div style={{ fontSize: 11.5, color: T.mute }}>{t.label.split(' — ')[1]}</div>}</td>
                    <td style={td}>{t.is_protected
                      ? <span style={pill('#F1EFE8', '#444441')}>Never — uses patient address</span>
                      : <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                          <input type="checkbox" checked={t.requires_facility} disabled={busy}
                            onChange={e => void run(() => updateSiteOfCareType(t.code, { requires_facility: e.target.checked }))} />
                          <span style={{ fontSize: 12.5 }}>{t.requires_facility ? 'Yes' : 'No'}</span>
                        </label>}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{t.requires_facility
                      ? <button style={{ background: 'none', border: 'none', color: T.primary, cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}
                          onClick={() => setTab(t.code)}>{t.facility_count}</button> : <span style={{ color: T.mute }}>—</span>}</td>
                    <td style={td}>{t.is_active ? <span style={pill(T.okBg, T.okFg)}>Active</span> : <span style={pill('#F1EFE8', '#444441')}>Inactive</span>}</td>
                    <td style={{ ...td, whiteSpace: 'normal', maxWidth: 260, fontSize: 12, color: T.soft }}>{t.notes}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button style={{ ...btnSm, marginRight: 6 }} onClick={() => setEditType({ ...t })}><Pencil size={11} /> Edit</button>
                      {t.is_protected
                        ? <span style={{ fontSize: 10.5, color: T.mute }}>protected</span>
                        : <button style={btnSm} disabled={busy}
                            onClick={() => void run(() => updateSiteOfCareType(t.code, { is_active: !t.is_active }))}>
                            {t.is_active ? 'Deactivate' : 'Reactivate'}</button>}
                    </td>
                  </tr>))}
              </tbody>
            </table>
          </div>
          <p style={{ margin: '10px 0 0', fontSize: 11.5, color: T.mute }}>
            Deactivating a type hides it from new enrollments only — existing therapies keep resolving it. Adding a type creates its facility list automatically.
            Types are never deleted: one printed on a signed enrollment form must render on that form forever.</p>
        </div>
      ) : (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, gap: 12, flexWrap: 'wrap' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 600 }}>{activeType ? activeType.label : 'All facilities'}</h3>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: T.soft }}>
                {total.toLocaleString()} {q || fx.active ? 'matching' : ''} facilit{total === 1 ? 'y' : 'ies'}
                {total > pageSize && <> · showing {((page - 1) * pageSize + 1).toLocaleString()}–{Math.min(page * pageSize, total).toLocaleString()}</>}
                {' '}· removal is a soft deactivate — a facility on a signed form is never deleted</p>
              {importDone && (
                <p style={{ margin: '6px 0 0', fontSize: 12.5, color: importDone.failed.length ? T.warnFg : T.okFg }}>
                  Imported <b>{importDone.added}</b> facilit{importDone.added === 1 ? 'y' : 'ies'}
                  {importDone.failed.length > 0 && <> · <b>{importDone.failed.length}</b> skipped: {importDone.failed.slice(0, 3).map(f => `${f.name} (${f.why})`).join('; ')}{importDone.failed.length > 3 && ` and ${importDone.failed.length - 3} more`}</>}
                </p>)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {/* Searches name, address, city and zip — on the SAME endpoint as the page, so what
                  you see is what the server matched, not a filtered slice of one page. */}
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search name, address, city, zip…"
                aria-label="Search facilities"
                style={{ ...input, width: 240 }} />
              {search && (
                <button style={{ ...btnSm }} onClick={() => setSearch('')}>Clear</button>
              )}
              <label style={{ fontSize: 12, color: T.soft, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} /> show inactive</label>
              {/* Import lands in the TAB YOU ARE ON — the file never carries the type. */}
              <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) void onPickFile(f); e.target.value = '' }} />
              <button style={btn} disabled={busy || importing} onClick={() => fileRef.current?.click()}
                title={`Import a CSV into ${activeType ? short(activeType.label) : 'this list'}`}>
                <Upload size={13} /> Import CSV</button>
              <button style={btnPrimary} disabled={busy}
                onClick={() => setEditFac({ facility_type: activeType?.code ?? facTypes[0]?.code })}>
                <Plus size={13} /> Add {activeType ? short(activeType.label) : 'facility'}</button>
            </div>
          </div>
          {/* Cap the HEIGHT, not the width: the header stays put and the page does not grow to 54
              rows tall. Horizontal scrolling stays on this container for narrow screens. */}
          {fx.chips}
          <div style={{ overflow: 'auto', maxHeight: '70vh', border: `0.5px solid ${T.border}`, borderRadius: 10 }}>
            <table {...fx.tableProps} style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr>
                {SortTh({ col: 'name', funnel: 'name', children: 'Name' })}
                {!activeType && SortTh({ col: 'type', funnel: 'type', children: 'Type' })}
                <th style={th}>Address{fx.funnel('address')}</th>
                {SortTh({ col: 'city', funnel: 'city_state_zip', children: 'City / State / Zip' })}
                <th style={th}>Phone{fx.funnel('phone')}</th>
                {showResidential && <th style={th}>Wing / Room / Bed{fx.funnel('wing_room_bed')}</th>}
                <th style={th}>Contact{fx.funnel('contact')}</th><th style={th}>Hours{fx.funnel('hours')}</th>
                <th style={th}>Delivery cutoff{fx.funnel('delivery_cutoff')}</th>
                <th style={th}>Source{fx.funnel('source')}</th><th style={{ ...th, textAlign: 'center' }}>Rx mapped{fx.funnel('rx_mapped')}</th>
                {SortTh({ col: 'used_by', funnel: 'used_by', children: 'Used by' })}
                <th style={th}>Status{fx.funnel('status')}</th><th data-no-funnel="row actions" style={{ ...th, textAlign: 'right' }}>Actions</th></tr></thead>
              <tbody>
                {listFor(activeType?.code ?? null).map(f => (
                  <tr key={f.id} style={{ opacity: f.is_active ? 1 : .5 }}>
                    <td style={td}><b>{f.name}</b></td>
                    {!activeType && <td style={{ ...td, fontSize: 12 }}>{short(types.find(t => t.code === f.facility_type)?.label ?? f.facility_type ?? '—')}</td>}
                    <td style={td}>{f.address_line ?? '—'}</td>
                    <td style={td}>{f.city ? `${f.city}, ${f.state_code ?? ''} ${f.zip_code ?? ''}` : '—'}</td>
                    <td style={td}>{f.phone ?? '—'}</td>
                    {showResidential && <td style={td}>{[f.wing, f.room, f.bed].filter(Boolean).join(' / ') || '—'}</td>}
                    <td style={{ ...td, whiteSpace: 'normal', minWidth: 160 }}>{f.contact_name
                      ? <><b>{f.contact_name}</b>{f.contact_phone && <div style={{ fontSize: 12 }}>{f.contact_phone}</div>}{f.contact_email && <div style={{ fontSize: 12, color: T.soft }}>{f.contact_email}</div>}</>
                      : <span style={{ color: T.mute }}>—</span>}</td>
                    <td style={{ ...td, whiteSpace: 'normal', fontSize: 12 }}>{f.operating_hours ?? '—'}</td>
                    <td style={td}>{f.delivery_cutoff_time ?? '—'}</td>
                    <td style={td}>{f.source === 'pioneer'
                      ? <span style={pill(T.okBg, T.okFg)}>PioneerRx</span>
                      : <span style={pill('#F1EFE8', '#444441')}>Pharmacy-managed</span>}</td>
                    <td style={{ ...td, textAlign: 'center' }} title={rxByFacility[f.id] ? `${rxByFacility[f.id].patients} patient(s)` : 'No prescription currently maps to this address'}>
                      {rxByFacility[f.id] ? <b>{rxByFacility[f.id].rx.toLocaleString()}</b> : <span style={{ color: T.mute }}>0</span>}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{f.used_by}</td>
                    <td style={td}>{f.is_active ? <span style={pill(T.okBg, T.okFg)}>Active</span> : <span style={pill('#F1EFE8', '#444441')}>Inactive</span>}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button style={{ ...btnSm, marginRight: 6 }} onClick={() => setEditFac({ ...f })}><Pencil size={11} /> Edit</button>
                      <button style={btnSm} disabled={busy} onClick={() => toggleFac(f)}>{f.is_active ? 'Deactivate' : 'Reactivate'}</button>
                    </td>
                  </tr>))}
                {/* "None yet · add the first one" is WRONG for a search that matched nothing —
                    the directory is not empty, the query is. Say which. */}
                {listFor(activeType?.code ?? null).length === 0 && (
                  <tr><td colSpan={15} style={{ ...td, textAlign: 'center', color: T.mute, padding: 26 }}>
                    {!q && fx.active ? (<>
                      No facilities match these filters{activeType ? <> in {short(activeType.label)}</> : null}.{' '}
                      <button style={{ background: 'none', border: 'none', color: T.primary, cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}
                        onClick={fx.clear}>Clear the filters →</button>
                    </>) : q ? (<>
                      No facilities match <b>{q}</b>{activeType ? <> in {short(activeType.label)}</> : null}.{' '}
                      <button style={{ background: 'none', border: 'none', color: T.primary, cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}
                        onClick={() => setSearch('')}>Clear the search →</button>
                    </>) : (<>
                    No {activeType ? short(activeType.label) : ''} facilities yet.{' '}
                    <button style={{ background: 'none', border: 'none', color: T.primary, cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}
                      onClick={() => setEditFac({ facility_type: activeType?.code ?? facTypes[0]?.code })}>Add the first one →</button>
                    </>)}
                  </td></tr>)}
              </tbody>
            </table>
          </div>

          {/* Pager. Rendered whenever there is more than one page — the server decides what a
              page contains, so these buttons only move `page` and let the effect refetch. */}
          {total > pageSize && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 12.5, color: T.soft }}>
                Page <b>{page}</b> of <b>{Math.max(1, Math.ceil(total / pageSize))}</b>
                {loadingList && <span style={{ marginLeft: 8, color: T.mute }}>loading…</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {/* Shared kit, not a hand-rolled dropdown — Gate 19 (E-rules) caught this one,
                    correctly: new UI uses SearchableSelect. */}
                <div style={{ fontSize: 12, color: T.soft, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  rows
                  <div style={{ width: 86 }}>
                    <SearchableSelect
                      value={String(pageSize)}
                      onChange={(v: string) => { setPageSize(Number(v)); setPage(1) }}
                      ariaLabel="Rows per page"
                      options={[25, 50, 100, 200].map(n => ({ value: String(n), label: String(n) }))} />
                  </div>
                </div>
                <button style={btnSm} disabled={page <= 1 || loadingList} onClick={() => setPage(1)}>« First</button>
                <button style={btnSm} disabled={page <= 1 || loadingList} onClick={() => setPage(p => Math.max(1, p - 1))}>‹ Prev</button>
                <button style={btnSm} disabled={page >= Math.ceil(total / pageSize) || loadingList}
                        onClick={() => setPage(p => p + 1)}>Next ›</button>
                <button style={btnSm} disabled={page >= Math.ceil(total / pageSize) || loadingList}
                        onClick={() => setPage(Math.max(1, Math.ceil(total / pageSize)))}>Last »</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── type modal ── */}
      {editType && (
        <Overlay onClose={() => setEditType(null)}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>{editType.isNew ? 'Add site-of-care type' : `Edit ${short(editType.label ?? '')}`}</h3>
          <label style={fieldLabel}>Label
            <input style={input} value={editType.label ?? ''} onChange={e => setEditType({ ...editType, label: e.target.value })}
              placeholder='e.g. "Hospital Outpatient"' /></label>
          {!editType.is_protected && (
            <label style={{ ...fieldLabel, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={editType.requires_facility ?? false}
                onChange={e => setEditType({ ...editType, requires_facility: e.target.checked })} />
              Requires a facility from the directory
            </label>)}
          {!editType.isNew && (
            <label style={fieldLabel}>Notes
              <input style={input} value={editType.notes ?? ''} onChange={e => setEditType({ ...editType, notes: e.target.value })} /></label>)}
          {editType.is_protected && <p style={{ fontSize: 12, color: T.mute, margin: '0 0 10px' }}>
            Home is protected: it always resolves to the patient's own address, so it can't require a facility or be deactivated.</p>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <button style={btn} onClick={() => setEditType(null)}>Cancel</button>
            <button style={btnPrimary} disabled={busy || !editType.label?.trim()} onClick={() => void saveTypeModal()}>
              {editType.isNew ? 'Add type' : 'Save changes'}</button>
          </div>
        </Overlay>)}

      {/* ── CSV import preview ──────────────────────────────────────────────────────────────
           Shows EXACTLY what will be written, before anything is written. The CDI list has
           mangled names, addresses split across rows and a duplicated facility; the preview is
           where those become visible rather than becoming your directory. */}
      {importRows && (
        <Overlay onClose={() => setImportRows(null)} wide>
          <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600 }}>
            Import {importRows.length - importSkip.size} facilit{importRows.length - importSkip.size === 1 ? 'y' : 'ies'} into {activeType ? short(activeType.label) : 'this list'}
          </h3>
          <p style={{ margin: '0 0 10px', fontSize: 12.5, color: T.soft }}>
            From <b>{importName}</b>. Values are imported <b>exactly as written</b> — nothing is cleaned up or
            merged. Type comes from the tab you are on, not the file. Edit anything in the table afterwards.
          </p>
          <div style={{ maxHeight: '52vh', overflow: 'auto', border: `0.5px solid ${T.border}`, borderRadius: 10 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr>
                <th style={{ ...th, width: 34 }} title="Untick to leave a row out"></th>
                <th style={th}>Name</th><th style={th}>Address</th><th style={th}>City</th>
                <th style={th}>State</th><th style={th}>Zip</th><th style={th}>Phone</th>
              </tr></thead>
              <tbody>
                {importRows.map((r, i) => (
                  <tr key={i} style={{ opacity: importSkip.has(i) ? .45 : 1 }}>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <input type="checkbox" checked={!importSkip.has(i)} onChange={() => setImportSkip(prev => {
                        const n = new Set(prev); if (n.has(i)) n.delete(i); else n.add(i); return n
                      })} /></td>
                    <td style={{ ...td, whiteSpace: 'normal' }}>
                      <b>{r.name}</b>
                      {!r.address_line && <div style={{ fontSize: 11, color: T.warnFg }}>no address — looks like a section heading, not a facility</div>}
                    </td>
                    <td style={{ ...td, whiteSpace: 'normal' }}>{r.address_line ?? '—'}</td>
                    <td style={td}>{r.city ?? '—'}</td>
                    <td style={td}>{r.state_code ?? '—'}</td>
                    <td style={td}>{r.zip_code ?? '—'}</td>
                    <td style={td}>{r.phone ?? '—'}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button style={btn} disabled={importing} onClick={() => setImportRows(null)}>Cancel</button>
            <button style={btnPrimary} disabled={importing || importRows.length === importSkip.size}
                    onClick={() => void runImport()}>
              {importing ? 'Importing…' : `Import ${importRows.length - importSkip.size}`}</button>
          </div>
        </Overlay>)}

      {/* ── facility modal ── */}
      {editFac && (
        <Overlay onClose={() => setEditFac(null)} wide>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>{editFac.id ? editFac.name : 'Add facility'}</h3>
          {/* A 12-COLUMN grid, with each field spanning what its CONTENT needs.
              It was repeat(auto-fit, minmax(178px, 1fr)), whose own comment claimed three
              columns; at this dialog's 880px it actually produced FOUR equal tracks, so a
              two-character State and a ten-character Zip ("93534-2801") got the same ~178px box
              as "Facility name". Mario, 2026-09-22: "the form is trash" — and the Zip, the field
              that DECIDES a site-of-care match, was rendering clipped to "93".
              Equal tracks are the bug: address fields are not equal-width data. Below ~760px the
              grid collapses to a single column (the media query in soc-facility-form.css) rather
              than squeezing twelve tracks into a laptop window. */}
          <div className="soc-facility-form" style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '0 14px' }}>
            <label style={{ ...fieldLabel, gridColumn: 'span 5' }}>Facility name
              <input style={input} value={editFac.name ?? ''} onChange={e => setEditFac({ ...editFac, name: e.target.value })} /></label>
            <label style={{ ...fieldLabel, gridColumn: 'span 4' }}>Type
              <SearchableSelect value={editFac.facility_type ?? ''} onChange={(v: string) => setEditFac({ ...editFac, facility_type: v })}
                ariaLabel="Facility type" placeholder="Select…"
                options={facTypes.map(t => ({ value: t.code, label: t.label }))} /></label>
            <label style={{ ...fieldLabel, gridColumn: 'span 7' }}>Address line
              <input style={input} value={editFac.address_line ?? ''} onChange={e => setEditFac({ ...editFac, address_line: e.target.value })} /></label>
            <label style={{ ...fieldLabel, gridColumn: 'span 3' }}>Phone
              <input style={input} value={editFac.phone ?? ''} onChange={e => setEditFac({ ...editFac, phone: e.target.value })} /></label>
            <label style={{ ...fieldLabel, gridColumn: 'span 5' }}>City
              <input style={input} value={editFac.city ?? ''} onChange={e => setEditFac({ ...editFac, city: e.target.value })} /></label>
            {/* State is two characters, Zip is up to ten ("93534-2801"). An even 1fr/1fr split
                gave both the same box and clipped the Zip to "93" on screen — the field that
                DECIDES a site-of-care match was the one you could not read. */}
            <div style={{ gridColumn: 'span 4', display: 'grid', gridTemplateColumns: '64px 1fr', gap: '0 10px' }}>
              <label style={fieldLabel}>State
                <input style={input} maxLength={2} placeholder="CA" value={editFac.state_code ?? ''}
                  onChange={e => setEditFac({ ...editFac, state_code: e.target.value.toUpperCase().slice(0, 2) })} /></label>
              <label style={fieldLabel}>Zip
                <input style={input} inputMode="numeric" placeholder="93534" value={editFac.zip_code ?? ''}
                  onChange={e => setEditFac({ ...editFac, zip_code: e.target.value })} /></label>
            </div>
            {/* D-SOC-3: who to call at the facility, when it is open, and when the last delivery must land */}
            <label style={{ ...fieldLabel, gridColumn: 'span 4' }}>Contact name
              <input style={input} value={editFac.contact_name ?? ''} onChange={e => setEditFac({ ...editFac, contact_name: e.target.value })} placeholder="e.g. Director of Nursing" /></label>
            <label style={{ ...fieldLabel, gridColumn: 'span 4' }}>Contact phone
              <input style={input} value={editFac.contact_phone ?? ''} onChange={e => setEditFac({ ...editFac, contact_phone: e.target.value })} /></label>
            <label style={{ ...fieldLabel, gridColumn: 'span 4' }}>Contact email
              <input style={input} value={editFac.contact_email ?? ''} onChange={e => setEditFac({ ...editFac, contact_email: e.target.value })} /></label>
            <label style={{ ...fieldLabel, gridColumn: 'span 6' }}>Operating hours
              <input style={input} value={editFac.operating_hours ?? ''} onChange={e => setEditFac({ ...editFac, operating_hours: e.target.value })} placeholder="e.g. Mon–Fri 7:00–19:00, Sat 8:00–14:00" /></label>
            <label style={{ ...fieldLabel, gridColumn: 'span 6' }}>Delivery cutoff time
              <input style={input} value={editFac.delivery_cutoff_time ?? ''} onChange={e => setEditFac({ ...editFac, delivery_cutoff_time: e.target.value })} placeholder="e.g. 15:00 — deliveries after this go to next day" /></label>
            {RESIDENTIAL.includes(editFac.facility_type ?? '') && (<>
              <label style={{ ...fieldLabel, gridColumn: 'span 4' }}>Wing
                <input style={input} value={editFac.wing ?? ''} onChange={e => setEditFac({ ...editFac, wing: e.target.value })} /></label>
              <div style={{ gridColumn: 'span 4', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
                <label style={fieldLabel}>Room
                  <input style={input} value={editFac.room ?? ''} onChange={e => setEditFac({ ...editFac, room: e.target.value })} /></label>
                <label style={fieldLabel}>Bed
                  <input style={input} value={editFac.bed ?? ''} onChange={e => setEditFac({ ...editFac, bed: e.target.value })} /></label>
              </div>
            </>)}
          </div>
          <p style={{ fontSize: 11.5, color: T.mute, margin: '2px 0 0' }}>
            Every change is audited — a facility address change is a shipping-destination change. Enrollments snapshot
            the name and address at use, so edits never rewrite a signed form.</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button style={btn} onClick={() => setEditFac(null)}>Cancel</button>
            <button style={btnPrimary} disabled={busy} onClick={() => void saveFacModal()}>{editFac.id ? 'Save changes' : 'Add facility'}</button>
          </div>
        </Overlay>)}
    </div>
  )
}

function Overlay({ children, onClose, wide }: { children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
      {/* 880 wide so the facility form's three columns have room; 92vw still governs a small
          window. overflow stays auto as the last-resort guard — the form is now short enough not
          to reach it, but a future field should scroll rather than escape the viewport. */}
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: 12, padding: '1.25rem', width: wide ? 880 : 440, maxWidth: '92vw', maxHeight: '90vh', overflow: 'auto' }}>{children}</div>
    </div>
  )
}
